import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { fetchGuidePreview } from '../../../../api/stay-guide-public-client';
import { GuideOpenBody, GuidePreviewStage } from '../../../../interfaces/stay-guide';
import { Staleable, StateSwitch } from '../../../../motion';
import { palette, radius, shadow, spacing, typography } from '../../../../style-utils/theme';
import { up } from '../../../../style-utils/media-queries';
import { SegmentedControl, SegmentOption } from '../../../segmented-control/segmented-control';
import { LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { t } from '../../../onboarding/stay/stay-strings';
import { GuideNav, GuideNavContext, GuideTarget } from '../guest/guide/guide-frame';
import { GuideOpenView } from '../guest/guide/guide-open-view';
import { HubSkeleton } from '../guest/guide/guest-guide-page';
import { Notice, NoticeText } from '../guest/guide/guide.styled';

const L = 'en' as const;

/**
 * THE HOST PREVIEW (plan S7.9, K-100) — the Preview tab of the guide editor.
 *
 * A ribbon that says what this is, a "Show the guide as" switch over four
 * moments of a stay, and below it the GUEST's own components fed by the
 * staff preview endpoint (GET /stays/listings/:id/guide/preview?stage=, which
 * builds the guest's body with the guest's own builder). Sections switch in
 * this page's state — no route, no guest link — and Share is disabled with its
 * reason. The preview NEVER calls a guest endpoint (no token exists here).
 *
 * A stage change keeps the guide on screen, dimmed, until the next answer
 * (Staleable): a refetch is never a skeleton.
 */

const STAGES: ReadonlyArray<SegmentOption<GuidePreviewStage>> = [
  { value: 'before_reveal', label: t(L, 'guide.previewBeforeReveal') },
  { value: 'released', label: t(L, 'guide.previewReleased') },
  { value: 'during_stay', label: t(L, 'guide.previewDuring') },
  { value: 'checkout_day', label: t(L, 'guide.previewCheckout') },
];

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  min-width: 0;
`;

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  min-width: 0;
`;

const ControlLabel = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.inkSoft};
`;

/** The guest's page, as a phone-width card on the dashboard. */
const Device = styled.div`
  width: 100%;
  max-width: calc(${spacing.huge} * 8);
  min-width: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.lg};
  background: ${palette.surface};
  box-shadow: ${shadow.md};

  ${up('sm')} {
    padding: ${spacing.lg};
  }
`;

type View = 'loading' | 'error' | 'ready';

export const GuidePreview: React.FC<{ listingId: string }> = ({ listingId }) => {
  const [stage, setStage] = useState<GuidePreviewStage>('before_reveal');
  const [section, setSection] = useState<GuideTarget>('hub');
  const [view, setView] = useState<View>('loading');
  const [body, setBody] = useState<GuideOpenBody | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const seq = useRef(0);
  const hasBody = useRef(false);

  const load = useCallback(() => {
    const mine = ++seq.current;
    if (hasBody.current) setRefreshing(true);
    else setView('loading');
    fetchGuidePreview(listingId, stage)
      .then(data => {
        if (mine !== seq.current) return;
        hasBody.current = true;
        setBody(data);
        setView('ready');
        setRefreshing(false);
      })
      .catch(() => {
        if (mine !== seq.current) return;
        setRefreshing(false);
        hasBody.current = false;
        setBody(null);
        setView('error');
      });
  }, [listingId, stage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(
    () => () => {
      seq.current += 1;
    },
    []
  );

  const nav = useMemo<GuideNav>(
    () => ({ preview: true, token: '', current: section, go: setSection }),
    [section]
  );

  return (
    <Wrap data-guide-preview="">
      <Notice $tone="neutral" role="note">
        <NoticeText>{t(L, 'guide.previewRibbon')}</NoticeText>
      </Notice>

      <Controls>
        <ControlLabel aria-hidden="true">{t(L, 'guide.previewShowAs')}</ControlLabel>
        <SegmentedControl
          options={STAGES}
          value={stage}
          onChange={setStage}
          label={t(L, 'guide.previewShowAs')}
        />
      </Controls>

      <StateSwitch state={view}>
        {view === 'loading' ? (
          <Device>
            <HubSkeleton />
          </Device>
        ) : view === 'error' || !body ? (
          <LoadError message={t(L, 'guide.previewError')} onRetry={load} retryLabel={t(L, 'tryAgain')} />
        ) : (
          <GuideNavContext.Provider value={nav}>
            <Staleable stale={refreshing}>
              <Device data-preview-stage={stage}>
                <GuideOpenView body={body} section={section} />
              </Device>
            </Staleable>
          </GuideNavContext.Provider>
        )}
      </StateSwitch>
    </Wrap>
  );
};
